// Sports related types

export interface Sport {
  id: number;
  name: string;
  description?: string;
  icon?: string;
  isActive: boolean;
  createdAt: string;
}

export interface Country {
  id: number;
  name: string;
  code: string;
  alpha2Code?: string;
  flag?: string;
  isActive: boolean;
  createdAt: string;
}

export interface League {
  id: number;
  name: string;
  shortName?: string;
  description?: string;
  level: number;
  isActive: boolean;
  createdAt: string;
  sportId: number;
  countryId: number;
  sport: Sport;
  country: Country;
  clubsCount?: number;
  seasonsCount?: number;
}

export interface PaginatedLeaguesResponse {
  leagues: League[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface Club {
  id: number;
  name: string;
  shortName?: string;
  description?: string;
  logo?: string;
  city?: string;
  stadium?: string;
  stadiumAddress?: string;
  founded?: string;
  isActive: boolean;
  createdAt: string;
  sportId: number;
  countryId: number;
  leagueId?: number;
  sport: Sport;
  country: Country;
  league?: League;
}

export interface Season {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  isCurrent: boolean;
  createdAt: string;
  leagueId: number;
  league: League;
}

export interface Competition {
  id: number;
  name: string;
  shortName?: string;
  description?: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  createdAt: string;
  seasonId: number;
  season: Season;
}

export interface Matchday {
  id: number;
  name: string;
  number: number;
  startDate?: string;
  endDate?: string;
  isCompleted: boolean;
  isActive: boolean;
  createdAt: string;
  seasonId: number;
  competitionId?: number;
  season: Season;
  competition?: Competition;
}

export interface Match {
  id: number;
  date: string; // Main date field for the match
  scheduledDate: string;
  actualDate?: string;
  homeScore?: number;
  awayScore?: number;
  status: MatchStatus;
  notes?: string;
  createdAt: string;
  matchdayId: number;
  // Nullable for own matches (Testspiel/Training) against non-master-data opponents
  homeClubId?: number;
  awayClubId?: number;
  matchday: Matchday;
  homeClub?: Club;
  awayClub?: Club;
  // Own-match (Testspiel/Training) fields
  matchType?: MatchType;
  homeTeamName?: string;
  awayTeamName?: string;
  location?: string;
  locationAddress?: string;
}

export enum MatchStatus {
  Scheduled = 0,
  Live = 1,
  Finished = 2,
  Postponed = 3,
  Cancelled = 4
}

export enum MatchType {
  League = 0,
  Friendly = 1,
  Training = 2
}

export interface UserPreferences {
  preferredSportId?: number;
  preferredClubId?: number;
  preferredSport?: Sport;
  preferredClub?: Club;
}

export interface UserDashboardData {
  user: {
    id: string;
    email: string;
    displayName?: string;
    preferredSport?: {
      id: number;
      name: string;
      icon?: string;
    };
    preferredClub?: {
      id: number;
      name: string;
      shortName?: string;
      city?: string;
      logo?: string;
      league?: {
        id: number;
        name: string;
        shortName?: string;
      };
      country: {
        id: number;
        name: string;
        flag?: string;
      };
    };
  };
  league?: {
    id: number;
    name: string;
    shortName?: string;
    currentSeason?: {
      id: number;
      name: string;
      startDate: string;
      endDate: string;
    };
    clubs: Array<{
      id: number;
      name: string;
      shortName?: string;
      city?: string;
      logo?: string;
    }>;
  };
}

export interface UpdateSportPreferenceRequest {
  sportId?: number;
}

export interface UpdateClubPreferenceRequest {
  clubId?: number;
}
